import { Injectable, OnDestroy } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { BehaviorSubject, Observable, Subject, map, takeUntil, of, switchMap } from 'rxjs';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { UserService } from 'app/core/user/user.service';

@Injectable({
    providedIn: 'root'
})
export class NotificationsService implements OnDestroy {
    private _notifications: BehaviorSubject<Notification[]> = new BehaviorSubject<Notification[]>([]);
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private _initialized = false;

    constructor(
        private _firestore: AngularFirestore,
        private _userService: UserService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    get notifications$(): Observable<Notification[]> {
        return this._notifications.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Initialize notifications (subscribe to Firestore)
     */
    getAll(): Observable<Notification[]> {
        if (this._initialized) {
            return this._notifications.asObservable();
        }

        this._userService.user$
            .pipe(
                takeUntil(this._unsubscribeAll),
                switchMap(user => {
                    if (!user?.uid) {
                        this._notifications.next([]);
                        return of([]);
                    }

                    return this._firestore
                        .collection('notifications', ref =>
                            ref.where('userId', '==', user.uid)
                               .orderBy('time', 'desc')
                        )
                        .valueChanges({ idField: 'id' }) as Observable<any[]>;
                })
            )
            .subscribe(notifications => {
                // Transform Firestore data to Notification format
                const transformed = notifications.map(n => ({
                    id: n.id,
                    icon: n.icon || 'heroicons_solid:bell',
                    title: n.title,
                    description: n.description,
                    time: n.time?.toDate ? n.time.toDate().toISOString() : n.time,
                    link: n.link,
                    useRouter: n.useRouter ?? true,
                    read: n.read ?? false
                }));
                this._notifications.next(transformed);
            });

        this._initialized = true;
        return this._notifications.asObservable();
    }

    /**
     * Mark a notification as read
     */
    update(id: string, notification: Partial<Notification>): Observable<Notification> {
        this._firestore.collection('notifications').doc(id).update({ read: notification.read });

        // Update local state
        const current = this._notifications.getValue();
        const index = current.findIndex(n => n.id === id);
        if (index !== -1) {
            current[index] = { ...current[index], ...notification };
            this._notifications.next([...current]);
        }

        return of(current[index]);
    }

    /**
     * Delete not supported (handled by backend)
     */
    delete(id: string): Observable<boolean> {
        // Notifications are not deletable by users
        return of(false);
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): Observable<boolean> {
        const current = this._notifications.getValue();
        const unread = current.filter(n => !n.read);

        if (unread.length === 0) {
            return of(true);
        }

        // Batch update in Firestore
        const batch = this._firestore.firestore.batch();
        unread.forEach(n => {
            const notificationRef = this._firestore.collection('notifications').doc(n.id).ref;
            batch.update(notificationRef, { read: true });
        });

        batch.commit().catch(err => console.error('Error marking all as read:', err));

        // Update local state
        const updated = current.map(n => ({ ...n, read: true }));
        this._notifications.next(updated);

        return of(true);
    }

    /**
     * Reset on logout
     */
    reset(): void {
        this._notifications.next([]);
        this._initialized = false;
    }
}
